// src/pages/SignupScreen.js
//
// Pass hosting/UX — a clean, separate public sign-up that collects AUTHENTICATION ESSENTIALS ONLY (name,
// email, password). No age/country/sport/avatar/photo here — that legacy onboarding happens afterwards in
// the existing /complete-profile flow.
//
// Pass 58G4 — INVITE-AWARE MODE. A first-time invited SAS staff member reaches this screen via
// /signup?redirect=%2Finvite%2F<code>&email=<invited-email>. In that mode we prefill and LOCK the invited
// email (so the account is created under the correct identity), do NOT stamp role:'student' (canonical
// membership/invite controls access; the projectMemberRole trigger projects ops after acceptance), and
// navigate BACK to the invite to accept — never to /complete-profile. Normal public signup is unchanged.

import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import AuthShell, { AUTH_T as T } from '../components/auth/AuthShell';

const friendly = (code = '') => ({
  'auth/email-already-in-use': 'That email already has an account. Try signing in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Choose a password of at least 6 characters.',
  'auth/network-request-failed': 'Network problem. Check your connection and try again.',
}[code]);

const input = { width: '100%', boxSizing: 'border-box', padding: '11px 12px', fontSize: 14.5, fontFamily: T.f, border: `1px solid ${T.borderStrong}`, borderRadius: 10, background: '#fff', color: T.ink, outline: 'none' };
const label = { display: 'block', fontSize: 12.5, fontWeight: 600, color: T.ink2, margin: '0 0 6px' };

// An invite-driven signup is any redirect back to an /invite/<code> route.
const isInviteRedirect = (redirect = '') => /^\/invite\//.test(String(redirect || ''));

export default function SignupScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirect = query.get('redirect') || '';
  const invitedEmail = query.get('email') || '';
  const inviteMode = isInviteRedirect(redirect);

  const [name, setName] = useState('');
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState(false); // invite mode: account already exists → offer sign in

  // Preserve redirect + invited email onto the sign-in / reset links so the invite context is never lost.
  const signInPath = useMemo(() => {
    const p = new URLSearchParams();
    if (redirect) p.set('redirect', redirect);
    if (invitedEmail) p.set('email', invitedEmail);
    const qs = p.toString();
    return qs ? `/login?${qs}` : '/login';
  }, [redirect, invitedEmail]);
  // §7 — the reset action lands on /login in reset mode (?reset=1), still carrying redirect+email.
  const resetPath = useMemo(() => (signInPath.includes('?') ? `${signInPath}&reset=1` : `${signInPath}?reset=1`), [signInPath]);

  const onSubmit = async (e) => {
    e.preventDefault(); if (busy) return; setError(''); setExisting(false); setBusy(true);
    // In invite mode the invited email is authoritative — never trust an edited value.
    const useEmail = (inviteMode && invitedEmail ? invitedEmail : email).trim();
    try {
      const cred = await createUserWithEmailAndPassword(auth, useEmail, password);
      if (name.trim()) { try { await updateProfile(cred.user, { displayName: name.trim() }); } catch { /* non-fatal */ } }
      if (inviteMode) {
        // Canonical staff onboarding: identity doc only — NO role:'student'. Access comes from the canonical
        // membership linked when the invite is accepted (and projectMemberRole projects the org role).
        await setDoc(doc(db, 'users', cred.user.uid), { name: name.trim(), email: useEmail, createdAt: serverTimestamp() }, { merge: true });
        navigate(redirect); // back to /invite/<code> to accept — NOT /complete-profile
        return;
      }
      // Normal public signup — student onboarding continues in /complete-profile.
      await setDoc(doc(db, 'users', cred.user.uid), { name: name.trim(), email: useEmail, role: 'student', createdAt: serverTimestamp() }, { merge: true });
      navigate('/complete-profile');
    } catch (err) {
      if (inviteMode && err?.code === 'auth/email-already-in-use') { setExisting(true); setError(''); }
      else setError(friendly(err?.code) || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div>
        <h2 style={{ fontFamily: T.fD, fontSize: 28, fontWeight: 800, margin: '0 0 6px' }}>{inviteMode ? 'Create your staff account' : 'Create your account'}</h2>
        <p style={{ fontSize: 14.5, color: T.ink2, margin: '0 0 24px' }}>
          {inviteMode ? 'Set a password to activate your invitation — then you’ll confirm and enter your workspace.' : 'Just the essentials to get started — you can add the rest next.'}
        </p>
        {existing ? (
          <div data-testid="invite-existing" style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, margin: '0 0 6px' }}>Your Voletus account already exists</div>
            <p style={{ fontSize: 14, color: T.ink2, margin: '0 0 16px' }}>
              There's already an account for <strong>{invitedEmail || email}</strong>. You don't need to create another one —
              sign in (or reset your password) and you'll return to your invitation to finish setup.
            </p>
            <Link to={signInPath} data-testid="existing-signin" style={{ display: 'block', textAlign: 'center', padding: '12px 14px', fontSize: 15, fontWeight: 700, color: '#fff', background: T.blue, borderRadius: 10, textDecoration: 'none', marginBottom: 10 }}>Sign in</Link>
            <Link to={resetPath} data-testid="existing-reset" style={{ display: 'block', textAlign: 'center', padding: '11px 14px', fontSize: 14.5, fontWeight: 700, color: T.ink, background: '#fff', border: `1px solid ${T.borderStrong}`, borderRadius: 10, textDecoration: 'none' }}>Reset password</Link>
          </div>
        ) : null}
        {!existing ? (
        <form onSubmit={onSubmit} noValidate>
          <label htmlFor="su-name" style={label}>Name</label>
          <input id="su-name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required style={{ ...input, marginBottom: 14 }} />
          <label htmlFor="su-email" style={label}>Email</label>
          <input
            id="su-email" type="email" autoComplete="email"
            value={inviteMode && invitedEmail ? invitedEmail : email}
            onChange={(e) => setEmail(e.target.value)}
            required
            readOnly={inviteMode && !!invitedEmail}
            data-testid="su-email"
            style={{ ...input, marginBottom: 14, ...(inviteMode && invitedEmail ? { background: T.border ? '#F3F4F6' : '#eee', color: T.ink2, cursor: 'not-allowed' } : {}) }}
          />
          {inviteMode && invitedEmail ? <div style={{ fontSize: 12, color: T.ink3, margin: '-8px 0 14px' }}>This is the email your invitation was sent to.</div> : null}
          <label htmlFor="su-password" style={label}>Password</label>
          <input id="su-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={input} />
          {error && <div data-testid="auth-error" role="alert" style={{ color: '#B91C1C', fontSize: 13, margin: '12px 0 0' }}>{error}</div>}
          <button type="submit" disabled={busy} style={{ width: '100%', padding: '12px 14px', fontSize: 15, fontWeight: 700, fontFamily: T.f, color: '#fff', background: busy ? '#9DB4F0' : T.blue, border: 'none', borderRadius: 10, cursor: busy ? 'default' : 'pointer', marginTop: 18 }}>{busy ? 'Creating account…' : (inviteMode ? 'Create account & continue' : 'Create account')}</button>
        </form>
        ) : null}
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${T.border}`, fontSize: 13.5, color: T.ink2 }}>
          <div>Already have an account? <Link to={inviteMode ? signInPath : '/login'} style={{ color: T.blue, fontWeight: 600, textDecoration: 'none' }}>Sign in</Link></div>
          {!inviteMode ? <div style={{ marginTop: 6 }}><strong style={{ color: T.ink }}>SAS staff:</strong> use the invitation link sent to you — don't create a separate account here.</div> : null}
        </div>
      </div>
    </AuthShell>
  );
}
